<template>
  <div class="custom-multiselect">
    <vue-multiselect
      class="select"
      :placeholder="placeholder"
      :value="_value"
      :options="options"
      :allow-empty="allowEmpty"
      :group-select="false"
      :group-label="groupLabel"
      :group-values="groupValues"
      :track-by="valueProperty"
      :label="displayProperty"
      select-label=""
      deselect-label=""
      :loading="loading"
      :disabled="disabled"
      :open-direction="openDirection"
      :options-limit="optionsLimit"
      :internal-search="internalSearch"
      :preserve-search="preserveSearch"
      :preselect-first="preselectFirst"
      :show-no-results="showNoResults"
      :taggable="taggable"
      @input="input"
      @close="close"
      @tag="tag"
      @search-change="search"
      @keydown.tab.native="tab"
      @keydown.enter.native="enter"
    >
      <div slot="noOptions">
        {{ noOptionsText }}
      </div>
      <div slot="noResult">
        {{ noResultsText }}
      </div>
    </vue-multiselect>
  </div>
</template>

<script>
  import VueMultiselect from "vue-multiselect";
  import "vue-multiselect/dist/vue-multiselect.min.css";

  const DD_DEFAULTS = {
    placeholder: "Select an option"
  };

  export default {
    name: "Select",
    props: {
      placeholder: {
        type: String,
        default: DD_DEFAULTS.placeholder
      },
      value: {
        type: [Number, String, Array]
      },
      options: {
        type: Array,
        required: true
      },
      allowEmpty: {
        type: Boolean,
        default: true
      },
      groupLabel: String,
      groupValues: String,
      displayProperty: {
        type: String,
        default: "label"
      },
      valueProperty: {
        type: String,
        default: "id"
      },
      loading: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      openDirection: {
        type: String,
        default: "",
        validator: value =>
          ["", "above", "top", "below", "bottom"].indexOf(value) !== -1
      },
      optionsLimit: {
        type: Number,
        default: 1000
      },
      internalSearch: {
        type: Boolean,
        default: true
      },
      preserveSearch: {
        type: Boolean,
        default: false
      },
      preselectFirst: {
        type: Boolean,
        default: false
      },
      showNoResults: {
        type: Boolean,
        default: true
      },
      noOptionsText: {
        type: String,
        default: "List is empty"
      },
      noResultsText: {
        type: String,
        default: "No results found"
      },
      multiple: {
        type: Boolean,
        default: false
      },
      tagPlaceholder: {
        type: String,
        default: "Add this as a new tag"
      },
      taggable: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      _value() {
        const { options, value, valueProperty } = this;

        if (value && valueProperty) {
          return options.find(option => option[valueProperty] === value);
        }

        return value;
      }
    },
    methods: {
      focus() {
        this.$el.focus();
      },
      input(value) {
        this.$emit("change", value);
      },
      search(searchQuery) {
        this.$emit("search", searchQuery);
      },
      close(event) {
        this.$emit("close", event);
      },
      tab(event) {
        this.$emit("tab", event);
      },
      tag(event) {
        this.$emit("tag", event);
      },
      enter(event) {
        event.stopPropagation();
      }
    },
    components: {
      VueMultiselect
    }
  };
</script>

<style lang="scss">
  .custom-multiselect {
    .multiselect__spinner:before,
    .multiselect__spinner:after {
      border-color: $color-secondary transparent transparent;
    }

    .multiselect,
    .multiselect__input::placeholder {
      color: #222;
    }

    .multiselect__tag {
      background: $color-secondary;
    }

    .multiselect__tag-icon:after {
      color: $color-purple-light;
    }

    .multiselect__tag-icon:focus,
    .multiselect__tag-icon:hover {
      background: $color-secondary;
    }

    .multiselect__option--highlight,
    .multiselect__option--highlight:after {
      background: $color-secondary;
    }

    .multiselect__option--selected.multiselect__option--highlight,
    .multiselect__option--selected.multiselect__option--highlight:after {
      background: $color-secondary;
    }

    .multiselect__option--group {
      color: #222;
    }

    .multiselect__option--group.multiselect__option--highlight,
    .multiselect__option--group.multiselect__option--highlight:after {
      background: #222;
    }

    .multiselect__option--group-selected.multiselect__option--highlight,
    .multiselect__option--group-selected.multiselect__option--highlight:after {
      background: $color-secondary;
    }
  }
</style>
